/**
 * <mj-product-carousel> -- the arrows behind sections/mj-product-carousel.liquid.
 *
 * Movement is native scroll-snap, so touch swipe, momentum, trackpad gestures and the
 * keyboard all work without being reimplemented. This file adds only what scroll-snap
 * does not give you: the two arrow buttons, and keeping their disabled state honest at
 * the ends of the track.
 *
 * Without this file the row is still fully usable -- it scrolls and snaps on its own,
 * and the arrows simply sit inert.
 */

class MjProductCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-mj-pcar-track]');
    this.prev = this.querySelector('[data-mj-pcar-prev]');
    this.next = this.querySelector('[data-mj-pcar-next]');
    if (!this.track) return;

    this.controller = new AbortController();
    const { signal } = this.controller;

    this.prev?.addEventListener('click', () => this.#step(-1), { signal });
    this.next?.addEventListener('click', () => this.#step(1), { signal });

    this.track.addEventListener('scroll', () => this.#queueSync(), { signal, passive: true });

    /*
     * The card width is a fraction of the track, so it changes with the viewport and at
     * the breakpoint. Re-checking on resize keeps the step size and the end states right
     * without hardcoding either.
     */
    this.resizeObserver = new ResizeObserver(() => this.#queueSync());
    this.resizeObserver.observe(this.track);

    this.#sync();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.frame);
  }

  /** Scroll fires far faster than paint; collapse bursts into one read per frame. */
  #queueSync() {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.#sync());
  }

  /** One page is whatever is currently visible, so the step follows the layout. */
  #step(direction) {
    this.track.scrollBy({ left: this.track.clientWidth * direction, behavior: 'smooth' });
  }

  #sync() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;

    // Sub-pixel scroll positions mean the end is never exactly reachable; a pixel of
    // slack stops the arrow flickering between states at the extremes.
    const atStart = scrollLeft <= 1;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;

    // Nothing to scroll at all: both arrows are meaningless, not merely at an end.
    const scrollable = scrollWidth > clientWidth + 1;

    this.#setDisabled(this.prev, !scrollable || atStart);
    this.#setDisabled(this.next, !scrollable || atEnd);
  }

  #setDisabled(button, disabled) {
    if (!button) return;
    button.disabled = disabled;
    // Disabled buttons are skipped by the tab order already; this keeps them out of the
    // accessibility tree's reading order too, so the row does not announce dead controls.
    button.setAttribute('aria-hidden', disabled ? 'true' : 'false');
  }
}

if (!customElements.get('mj-product-carousel')) {
  customElements.define('mj-product-carousel', MjProductCarousel);
}
